import { ApiErrorResponseWithCode } from '@vfde-sails/utils';
import {
    JarvisAvcExistingResponse,
    JarvisAvcResponse,
    JarvisAvcSuccessResponse,
    JarvisErrorResponse,
    JarvisPriceMap,
} from '../interface';
import { JarvisError } from '../enum';

/**
 * Checks if response is an existing cable product response
 */
export const isExistingCableProductResponse = (response: JarvisAvcResponse): response is JarvisAvcExistingResponse =>
    'customerInfo' in response
    && !!response.customerInfo
    && !!response.customerInfo.existingKipId;

/**
 * Checks if response is a success response
 */
export const isSuccessResponse = (response: JarvisAvcResponse): response is JarvisAvcSuccessResponse =>
    'products' in response && 'address' in response;

/**
 * Checks if error is captcha error
 */
export const isCaptchaError = (error: unknown): error is ApiErrorResponseWithCode<JarvisErrorResponse> =>
    error instanceof ApiErrorResponseWithCode
    && error.status === 401
    && error.body.errorCode === JarvisError.CAPTCHA;

/**
 * Checks if error is not found address error
 */
export const isAddressNotFoundError = (error: unknown): error is ApiErrorResponseWithCode<JarvisErrorResponse> =>
    error instanceof ApiErrorResponseWithCode
    && error.status === 400
    && error.body.errorCode === JarvisError.INVALID_ADDRESS;

/**
 * Checks if value is a JarvisPriceMap
 *
 * @param value - The value to be checked
 */
export const isJarvisPriceMap = (value: unknown): value is JarvisPriceMap => typeof value === 'object'
    && value !== null
    && Object.hasOwn(value, 'price')
    && Object.hasOwn(value, 'ids');

/**
 * Checks given variable if its a string ant not empty
 * and if an array is provided also checks whether it includes the value
 *
 * @param value - the variable to be validated
 * @param array - array the value should be included
 * @returns boolean
 */
export const isValidId = (value: unknown, array: string[]): value is string => isNotEmptyString(value) && array.includes(value);

/**
 * Checks given variable if its a string ant not empty
 *
 * @param value - the variable to be validated
 * @returns boolean
 */
export const isNotEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

/**
 * Checks if the result of the array.findIndex is -1 or not
 *
 * @param index - Result of an array.findIndex call.
 * @returns True if index is found, otherwise false.
 */
export const isIndexFound = (index: number): boolean => index !== -1;

/**
 * Checks whether the given text represents a number or not.
 *
 * @param text - Any text
 * @returns True if the text contains only numeric characters, otherwise false.
 */
export const isStringANumber = (text: string): boolean => !isNaN(+text);

/**
 * Checks if value is not null or undefined
 */
export const isNotNullOrUndefined = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;
